class CartLocalDeliveryCheck extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.form = this.querySelector('[data-local-delivery-form]');
    this.postcodeInput = this.querySelector('[data-local-delivery-postcode]');
    this.status = this.querySelector('[data-local-delivery-status]');
    this.actions = this.querySelector('[data-local-delivery-actions]');
    this.collectionLink = this.querySelector('[data-local-delivery-collection-link]');
    this.switchButton = this.querySelector('[data-local-delivery-switch]');
    this.routesUrl = this.dataset.routesUrl;
    this.collectionUrl = this.dataset.collectionUrl || '/collections/lokale-lieferung';
    this.config = this.readConfig();
    this.postcodes = new Set();

    if (!this.form || !this.postcodeInput || !this.status || !this.config) return;

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.evaluate();
    });
    this.switchButton?.addEventListener('click', () => this.switchToLocalDelivery());

    if (this.collectionLink) this.collectionLink.href = this.collectionUrl;
    this.loadCoverage();
  }

  readConfig() {
    const configElement = this.querySelector('[data-local-delivery-config]');
    if (!configElement) return null;

    try {
      return JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('Konfiguration für den lokalen Lieferwechsel konnte nicht gelesen werden.', error);
      return null;
    }
  }

  async loadCoverage() {
    try {
      const response = await fetch(this.routesUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const payload = await response.json();
      this.postcodes = new Set(
        Object.keys(payload.routes || {}).filter((postcode) => /^\d{5}$/.test(postcode)),
      );

      const defaultPostcode = this.normalizePostcode(this.dataset.defaultPostcode);
      if (defaultPostcode && !this.postcodeInput.value.trim()) {
        this.postcodeInput.value = defaultPostcode;
        this.evaluate(true);
      }
    } catch (error) {
      console.error('Liefergebiet für den Warenkorb konnte nicht geladen werden.', error);
      this.renderStatus(
        'error',
        'Die Prüfung des Liefergebiets ist gerade nicht verfügbar. Sie können den Versand wie gewohnt fortsetzen.',
      );
    }
  }

  normalizePostcode(value) {
    return String(value || '').replace(/\D/g, '').slice(0, 5);
  }

  evaluate(isDefaultAddress = false) {
    const postcode = this.normalizePostcode(this.postcodeInput.value);
    this.postcodeInput.value = postcode;

    if (!/^\d{5}$/.test(postcode)) {
      this.renderStatus('invalid', 'Bitte geben Sie eine fünfstellige Postleitzahl ein.');
      return false;
    }

    if (this.postcodes.has(postcode)) {
      const prefix = isDefaultAddress ? 'Ihre gespeicherte PLZ liegt' : 'Ihre PLZ liegt';
      this.renderStatus(
        'eligible',
        `${prefix} in unserem Liefergebiet. Lokale Lieferung ist möglich. Versandartikel werden nicht automatisch umgestellt.`,
      );
      this.actions.hidden = false;
      return true;
    }

    this.renderStatus(
      'outside',
      'Für diese PLZ ist aktuell keine lokale Lieferung hinterlegt. Sie können mit dem Versand fortfahren.',
    );
    return false;
  }

  renderStatus(state, message) {
    this.status.dataset.state = state;
    this.status.textContent = message;
    this.status.hidden = false;
    if (state !== 'eligible') this.actions.hidden = true;
  }

  async switchToLocalDelivery() {
    const lineNumbers = [...new Set(this.config.shippingLineNumbers || [])]
      .map((line) => Number(line))
      .filter((line) => Number.isInteger(line) && line > 0)
      .sort((a, b) => b - a);

    if (!lineNumbers.length) {
      window.location.href = this.collectionUrl;
      return;
    }

    const confirmed = window.confirm(
      'Die Versandartikel werden aus dem Warenkorb entfernt. Anschließend können Sie die passenden Produkte für lokale Lieferung auswählen. Fortfahren?',
    );
    if (!confirmed) return;

    if (this.switchButton) this.switchButton.disabled = true;

    try {
      for (const line of lineNumbers) {
        const request = typeof fetchConfig === 'function' ? fetchConfig() : { headers: { 'Content-Type': 'application/json' } };
        const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
          ...request,
          body: JSON.stringify({ line, quantity: 0 }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
      }

      window.location.href = this.collectionUrl;
    } catch (error) {
      console.error('Versandartikel konnten nicht aus dem Warenkorb entfernt werden.', error);
      this.renderStatus(
        'error',
        'Die Versandartikel konnten nicht entfernt werden. Bitte versuchen Sie es erneut oder wählen Sie die lokalen Produkte direkt aus.',
      );
      if (this.switchButton) this.switchButton.disabled = false;
    }
  }
}

if (!customElements.get('cart-local-delivery-check')) {
  customElements.define('cart-local-delivery-check', CartLocalDeliveryCheck);
}
